// TextPadDrawer.tsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Drawer,
  TextField,
  Typography,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import ArticleIcon from "@mui/icons-material/Article";
import CodeIcon from "@mui/icons-material/Code";
import ReactMarkdown from "react-markdown";
import { useUploadEnqueue } from "./app/transferQueue";
import { ensureFolderExists } from "./app/transfer";
import { FileItem, encodeKey } from "./FileGrid";

interface TextPadDrawerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  cwd: string;
  onUpload: () => void;
  editFile?: FileItem | null;
}

const generateDefaultName = () => {
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `note-${now.getFullYear().toString().slice(-2)}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}.md`;
};

const TextPadDrawer: React.FC<TextPadDrawerProps> = ({
  open,
  setOpen,
  cwd,
  onUpload,
  editFile,
}) => {
  const [noteText, setNoteText] = useState("");
  const [noteName, setNoteName] = useState(generateDefaultName);
  const [viewMode, setViewMode] = useState<"edit" | "preview">("edit");
  const [loading, setLoading] = useState(false);
  const uploadEnqueue = useUploadEnqueue();

  const isEditing = editFile !== null && editFile !== undefined;
  const isMarkdown = isEditing
    ? (editFile!.httpMetadata?.contentType === "text/markdown" ||
       editFile!.httpMetadata?.contentType === "text/x-markdown" ||
       editFile!.key.endsWith(".md") ||
       editFile!.key.endsWith(".markdown"))
    : true; // Default for new notes

  useEffect(() => {
    if (open) {
      if (isEditing && editFile) {
        // Load existing file content
        const fileUrl = `/webdav/${encodeKey(editFile.key)}`;
        setLoading(true);
        fetch(fileUrl)
          .then((res) => res.text())
          .then((text) => {
            setNoteText(text);
            setNoteName(editFile.key.split("/").pop() || "");
          })
          .catch(() => {
            setNoteText("Failed to load content");
          })
          .finally(() => setLoading(false));
      } else {
        // New note mode
        setNoteText("");
        setNoteName(generateDefaultName());
      }
      setViewMode("edit");
    }
  }, [open, isEditing, editFile]);

  const handleSaveNote = async () => {
    if (isEditing && editFile) {
      // Update existing file
      const fileUrl = `/webdav/${encodeKey(editFile.key)}`;
      await fetch(fileUrl, {
        method: "PUT",
        body: noteText,
        headers: {
          "Content-Type": editFile.httpMetadata?.contentType || "text/markdown",
        },
      });
    } else {
      // Create new note
      const notesDir = "notes/";
      await ensureFolderExists(notesDir);
      const fileBlob = new Blob([noteText], { type: "text/markdown" });
      const file = new File([fileBlob], noteName, { type: "text/markdown" });
      uploadEnqueue({ file, basedir: notesDir });
    }
    onUpload(); // Refresh file list after upload
    setOpen(false); // Close drawer
    setNoteText(""); // Reset
    setNoteName(generateDefaultName());
  };

  return (
    <Drawer anchor="right" open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { width: { xs: "100%", sm: 500, md: 600 } } }}>
      <Box sx={{ padding: 2, display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 1 }}>
            <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {isEditing ? <EditIcon /> : null}
              {isEditing ? "Edit File" : "TextPad"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
            {isMarkdown && noteText && (
              <ToggleButtonGroup
                size="small"
                value={viewMode}
                exclusive
                onChange={(_, value) => value && setViewMode(value)}
              >
                <ToggleButton value="preview" title="Rendered">
                  <ArticleIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="edit" title="Raw">
                  <CodeIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            <IconButton onClick={() => setOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <TextField
          label="File Name"
          value={noteName}
          onChange={(e) => setNoteName(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
          disabled={isEditing} // Can't rename when editing existing file
        />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : viewMode === "preview" && isMarkdown ? (
          <Box
            sx={(theme) => ({
              flexGrow: 1,
              backgroundColor: theme.palette.mode === "dark" ? "grey.900" : "grey.100",
              p: 2,
              borderRadius: 1,
              overflow: "auto",
              mb: 2,
              "& h1, & h2, & h3, & h4, & h5, & h6": { mt: 2, mb: 1 },
              "& p": { my: 1 },
              "& pre": {
                backgroundColor: theme.palette.mode === "dark" ? "grey.800" : "grey.200",
                p: 1,
                borderRadius: 1,
                overflow: "auto",
              },
              "& code": {
                backgroundColor: theme.palette.mode === "dark" ? "grey.800" : "grey.200",
                px: 0.5,
                borderRadius: 0.5,
                fontFamily: "monospace",
              },
              "& pre code": { backgroundColor: "transparent", p: 0 },
              "& blockquote": {
                borderLeft: 4,
                borderColor: "grey.400",
                pl: 2,
                ml: 0,
                color: "text.secondary",
              },
              "& ul, & ol": { pl: 3 },
              "& a": { color: "primary.main" },
            })}
          >
            <ReactMarkdown>{noteText}</ReactMarkdown>
          </Box>
        ) : (
          <TextField
            label={isEditing ? "Edit content..." : "Write your note..."}
            multiline
            variant="outlined"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            fullWidth
            sx={{
              flexGrow: 1,
              "& .MuiInputBase-root": {
                height: "100%",
                alignItems: "flex-start",
              },
              "& .MuiInputBase-input": {
                height: "100% !important",
                overflow: "auto !important",
                fontFamily: "monospace",
              },
            }}
          />
        )}

        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={handleSaveNote}
          disabled={!noteText.trim() || loading}
        >
          {isEditing ? "Save Changes" : "Save & Upload Note"}
        </Button>
      </Box>
    </Drawer>
  );
};

export default TextPadDrawer;
